import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../lib/AppContext";
import { NAV, t } from "../lib/i18n";
import { api, errMsg, rm, pnlClass } from "../lib/api";
import { ModeSwitch, ModeBanner } from "./AccountMode";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";

function Clock({ s }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={`session-${s.exchange.toLowerCase()}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.open ? "bg-emerald-400 pulse-dot" : "bg-slate-600"}`} />
      <span className="mono text-[10px] text-slate-600">{s.exchange}</span>
      <span className="mono text-[10px] text-slate-800">{s.local_time.slice(11)}</span>
    </div>
  );
}

function EmergencyStop() {
  const { lang, refresh, overview } = useApp();
  const halted = overview?.settings?.halt_new_orders;
  const [mfa, setMfa] = useState("");
  const release = async () => {
    try { await api.post("/emergency-stop/release", { mfa_code: mfa }); toast.success("Emergency stop released. Re-enable autonomous trading when ready."); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const fire = async () => {
    try { const r = await api.post("/emergency-stop"); toast.error(`Halted. ${r.data.cancelled_entry_orders} entry orders cancelled; ${r.data.protective_orders_kept} protective exits kept.`); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (halted) return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid="emergency-stop-button" className="mono flex items-center gap-1.5 whitespace-nowrap rounded-sm border border-red-900 bg-red-900/60 px-2 py-1.5 text-[11px] font-bold text-white sm:px-3"><Icons.OctagonX className="h-3.5 w-3.5" />HALTED</button>
      </AlertDialogTrigger>
      <AlertDialogContent className="border-[#E2E8F0] bg-white" data-testid="emergency-release-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Release emergency stop?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-600">New orders will be allowed again. Autonomous trading stays OFF until you re-enable it. MFA code required in LIVE mode.</AlertDialogDescription>
        </AlertDialogHeader>
        {overview?.settings?.trading_mode === "live" && <input data-testid="release-mfa-input" value={mfa} onChange={(e) => setMfa(e.target.value)} placeholder="MFA code" className="mono rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-1.5 text-xs" />}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="emergency-release-cancel" className="border-[#E2E8F0] bg-transparent text-slate-700">Keep halted</AlertDialogCancel>
          <AlertDialogAction data-testid="emergency-release-confirm" onClick={release} className="bg-emerald-600 text-white hover:bg-emerald-700">Release</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid="emergency-stop-button" className="mono flex animate-pulse items-center gap-1.5 whitespace-nowrap rounded-sm border border-red-500 bg-red-600 px-2 py-1.5 text-[11px] font-bold text-white shadow-lg shadow-red-900/30 transition-colors hover:bg-red-700 sm:px-3">
          <Icons.OctagonX className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t(lang, "emergency_stop")}</span><span className="sm:hidden">STOP</span>
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="border-[#E2E8F0] bg-white" data-testid="emergency-stop-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-red-600">Activate emergency stop?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-600">
            Immediately halts all NEW orders and autonomous entries, cancels pending entry orders. Existing protective stop-loss / take-profit exits stay active and are NOT cancelled.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="emergency-stop-cancel" className="border-[#E2E8F0] bg-transparent text-slate-700">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="emergency-stop-confirm" onClick={fire} className="bg-red-600 text-white hover:bg-red-700">Halt new orders</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Header({ onMenu }) {
  const { overview, lang, switchLang, logout, user, view } = useApp();
  const st = overview?.targets?.[view] || overview?.state;
  const prog = Math.max(0, Math.min(100, st?.target_progress_pct || 0));
  const locked = st && (st.target_lock || (st.day_start_equity > 0 && st.daily_pnl <= -st.day_start_equity * 0.01));
  return (
    <header className="sticky top-0 z-50 flex h-14 items-center justify-between gap-3 border-b border-[#E2E8F0] bg-white/95 px-3 backdrop-blur-md">
      <div className="flex items-center gap-3">
        <button className="lg:hidden" onClick={onMenu} data-testid="mobile-menu-btn"><Icons.Menu className="h-5 w-5 text-slate-700" /></button>
        <div className="flex items-center gap-2">
          <img src="/logo.svg" alt="DMI Share Investment" className="h-8 w-8" data-testid="app-logo" />
          <div className="hidden leading-none sm:block">
            <div className="font-display text-sm font-extrabold tracking-tight">DMI <span className="text-blue-600">SHARE</span> INVESTMENT</div>
            <div className="mono text-[9px] text-slate-500">PRIVATE OWNER TERMINAL</div>
          </div>
        </div>
        <ModeSwitch />
      </div>
      <div className="hidden items-center gap-4 xl:flex">{overview?.sessions?.map((s) => <Clock key={s.exchange} s={s} />)}</div>
      <div className="flex items-center gap-2 sm:gap-3">
        {st && (
          <div className="hidden w-44 md:block" data-testid="daily-profit-widget">
            <div className="flex justify-between text-[10px]">
              <span className="text-slate-500"><span data-testid="daily-target-mode" className={`mono mr-1 font-bold ${view === "live" ? "text-red-600" : "text-amber-600"}`}>{view === "live" ? "LIVE" : "DEMO"}</span>{t(lang, "target")} <span data-testid="daily-target-amount">{rm(st.daily_target)}</span></span>
              <span data-testid="daily-target-pnl" className={`mono whitespace-nowrap ${pnlClass(st.daily_pnl)}`}>{rm(st.daily_pnl)}</span>
            </div>
            <div className="mt-1 h-1 rounded-full bg-[#E2E8F0]"><div className="h-1 rounded-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${prog}%` }} /></div>
          </div>
        )}
        <span data-testid="shield-indicator" title="Capital Shield" className={`hidden items-center gap-1 text-[10px] sm:flex ${locked ? "text-red-600" : "text-emerald-600"}`}>
          <Icons.ShieldCheck className="h-4 w-4" />{locked ? "LOCK" : "OK"}
        </span>
        <div className="flex overflow-hidden rounded-sm border border-[#E2E8F0] text-[10px]">
          {["en", "ms"].map((l) => (
            <button key={l} data-testid={`lang-switch-${l}`} onClick={() => switchLang(l)} className={`px-2 py-1 ${lang === l ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-[#F1F5F9]"}`}>{l.toUpperCase()}</button>
          ))}
        </div>
        <EmergencyStop />
        <button onClick={logout} title={`${user?.email} · MFA ${user?.mfa_enabled ? "on" : "off"}`} data-testid="logout-btn" className="text-slate-600 hover:text-slate-900">
          <Icons.LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}

function Sidebar({ open, close }) {
  const { lang, overview } = useApp();
  return (
    <aside className={`${open ? "fixed inset-y-14 left-0 z-40 flex" : "hidden"} custom-scrollbar h-[calc(100vh-3.5rem)] w-60 shrink-0 flex-col overflow-y-auto border-r border-[#E2E8F0] bg-[#F8FAFC] lg:sticky lg:top-14 lg:flex`}>
      <nav className="flex flex-col py-2">
        {NAV.map((n) => {
          const I = Icons[n.icon] || Icons.Circle;
          return (
            <NavLink key={n.key} to={n.path} end={n.path === "/"} onClick={close} data-testid={`nav-item-${n.key}`}
              className={({ isActive }) => `group flex items-center gap-2.5 border-l-2 px-4 py-2 text-[12px] transition-colors duration-150 ${isActive ? "border-blue-500 bg-white text-slate-900" : "border-transparent text-slate-600 hover:bg-white hover:text-slate-800"}`}>
              <I className="h-3.5 w-3.5" />
              <span className="flex-1">{lang === "ms" ? n.ms : n.en}</span>
              {n.key === "notifications" && overview?.unread_notifications > 0 && <span className="mono rounded-sm bg-blue-600 px-1 text-[9px] text-white">{overview.unread_notifications}</span>}
            </NavLink>
          );
        })}
      </nav>
      <div className="mt-auto border-t border-[#E2E8F0] p-3 text-[10px] leading-relaxed text-slate-500">{t(lang, "disclaimer")}</div>
    </aside>
  );
}

function useFillToasts(user) {
  useEffect(() => {
    if (!user) return undefined;
    let since = null;
    const tick = async () => {
      try {
        const r = await api.get(`/moomoo/events${since ? `?since=${encodeURIComponent(since)}` : "?limit=1"}`);
        if (since) r.data.events.filter((e) => e.event_type === "EVENT_FILL").forEach((e) => toast.success(`Moomoo fill: ${e.side} ${e.code} ${e.dealt_qty} @ ${e.dealt_avg_price} (${e.order_status})`));
        since = r.data.events[0]?.ts || since || r.data.server_time;
      } catch { /* push feed optional */ }
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => clearInterval(t);
  }, [user]);
}

export default function Layout() {
  const { user } = useApp();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const here = window.location.pathname + window.location.search;
    if (user === false) nav(here === "/" ? "/welcome" : `/login?next=${encodeURIComponent(here)}`, { replace: true });
    else if (user?.role === "member") nav("/member", { replace: true });
  }, [user, nav]);
  useFillToasts(user?.role === "member" ? null : user);
  if (!user || user.role === "member") return <div className="grid h-screen place-items-center bg-white text-slate-500"><Icons.Loader2 className="animate-spin" /></div>;
  return (
    <div className="grain min-h-screen bg-white text-slate-900">
      <Header onMenu={() => setOpen((o) => !o)} />
      <ModeBanner />
      <div className="flex">
        <Sidebar open={open} close={() => setOpen(false)} />
        <main className="custom-scrollbar relative z-10 min-w-0 flex-1 p-3 sm:p-4"><Outlet /></main>
      </div>
    </div>
  );
}
